/**
 * Authorization error types from backend
 */
export type AuthorizationErrorType = 
  | 'account_not_registered'    // 401: User in Entra but not in backend DB
  | 'account_disabled'          // 403: User account disabled
  | 'insufficient_permissions'  // 403: User lacks required role
  | 'resource_access_denied'    // 403: Not resource owner
  | 'profile_not_found'         // 404: First time user
  | 'service_unavailable';      // 404: Backend service not available

/**
 * Extended error information for authorization failures
 */
export interface AuthorizationError extends Error {
  type: AuthorizationErrorType;
  status: 401 | 403 | 404;
  originalMessage: string;
  userMessage: string;
  action: 'switch_account' | 'contact_support' | 'create_profile' | 'retry';
  metadata?: {
    accountEmail?: string;
    accountId?: string;
    requiredRole?: string;
    currentRoles?: string[];
  };
  backendError?: ErrorResponse; // Original backend error
}

/**
 * Backend error response format (from backend team)
 */
export interface ErrorResponse {
  error: string;
  statusCode: number;
  requiredRole?: string;
  details?: any;
}

/**
 * Type guard for authorization errors
 */
export function isAuthorizationError(error: any): error is AuthorizationError {
  return error && 
    typeof error === 'object' && 
    'type' in error && 
    'status' in error &&
    (error.status === 401 || error.status === 403 || error.status === 404);
}

/**
 * Factory function to create AuthorizationError from backend response
 */
export function createAuthorizationError(
  status: number,
  backendError: ErrorResponse,
  accountInfo?: { email?: string; id?: string }
): AuthorizationError {
  let type: AuthorizationErrorType;
  let userMessage: string;
  let action: AuthorizationError['action'];

  // Determine error type based on status and backend response
  if (status === 401) {
    type = 'account_not_registered';
    userMessage = 'Your account is not registered in the system. Please sign in with an authorized account or contact support.';
    action = 'switch_account';
  } else if (status === 404 && backendError.error.toLowerCase().includes('profile')) {
    type = 'profile_not_found';
    userMessage = 'No profile found. This appears to be your first time. Would you like to create your profile?';
    action = 'create_profile';
  } else if (status === 404) {
    // 404 without profile message indicates backend is not available
    type = 'service_unavailable';
    userMessage = 'The service is currently unavailable. Please try again later.';
    action = 'retry';
  } else if (status === 403) {
    if (backendError.requiredRole) {
      type = 'insufficient_permissions';
      userMessage = `You need the '${backendError.requiredRole}' role to access this feature. Please contact your administrator.`;
      action = 'contact_support';
    } else if (backendError.error.toLowerCase().includes('disabled')) {
      type = 'account_disabled';
      userMessage = 'Your account has been disabled. Please contact support for assistance.';
      action = 'contact_support';
    } else {
      type = 'resource_access_denied';
      userMessage = 'You do not have permission to access this resource.';
      action = 'switch_account';
    }
  } else {
    type = 'resource_access_denied';
    userMessage = 'Access denied. Please try a different account.';
    action = 'switch_account';
  }

  const error = new Error(userMessage) as AuthorizationError;
  error.name = 'AuthorizationError';
  error.type = type;
  error.status = status as 401 | 403 | 404;
  error.originalMessage = backendError.error;
  error.userMessage = userMessage;
  error.action = action;
  error.backendError = backendError;
  error.metadata = {
    accountEmail: accountInfo?.email,
    accountId: accountInfo?.id,
    requiredRole: backendError.requiredRole
  };

  return error;
}
